/**
 * ErrorBoundary — per-route crash cushion.
 *
 * A render crash inside a route (e.g. an unexpected shape from the API,
 * a null-deref, an exception in a third-party lib) should not nuke the
 * entire app shell. We wrap each ``<Route element>`` in
 * ``RouteErrorBoundary`` so the sidebar, top bar, and every OTHER route
 * stay interactive — the user can click Home and retry the broken page.
 *
 * Three levers live here:
 *
 *  - ``ErrorBoundary``      — the class component. Catches, tracks a
 *                             retry counter, surfaces a "Try again" button
 *                             that re-mounts children, and a "Go home"
 *                             link for the case where retry obviously
 *                             won't help.
 *  - ``RouteErrorBoundary`` — function wrapper that keys the boundary
 *                             on the current pathname. Navigating to a
 *                             different route AUTOMATICALLY resets the
 *                             crash state — otherwise "click sidebar →
 *                             same error screen" would be a frequent
 *                             confusing bug.
 *  - ``QueryErrorState``    — inline (non-boundary) retry UI for
 *                             pages whose fetch failed. An async fetch
 *                             rejection is NOT a render crash, so the
 *                             boundary never sees it; pages should
 *                             render this component in its place.
 */
import { Component, type ErrorInfo, type ReactNode, useState } from "react";
import { AlertTriangle, Copy, Home, RefreshCw, WifiOff } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/Button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Extra context (e.g. route name) threaded into the error report. */
  scope?: string;
  /** Called when an error is caught — wire this up to Sentry / Datadog. */
  onError?: (error: Error, info: ErrorInfo, scope?: string) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  // Incremented on every Try Again click — used as a React ``key`` on
  // the rendered children so the next attempt remounts them fresh.
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, retryCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console in every env so ops can read the stack in browser devtools;
    // ``onError`` fans out to Sentry etc. when wired.
    console.error(
      `[ErrorBoundary${this.props.scope ? `:${this.props.scope}` : ""}]`,
      error,
      info
    );
    try {
      this.props.onError?.(error, info, this.props.scope);
    } catch {
      // Never let the reporter itself crash the fallback render.
    }
  }

  private handleRetry = () => {
    // Bumping retryCount + clearing hasError causes children to re-mount.
    // We bump even when ``hasError`` stays true defensively so a repeat
    // crash doesn't silently reuse the stale React tree.
    this.setState((s) => ({
      hasError: false,
      error: null,
      retryCount: s.retryCount + 1,
    }));
  };

  private copyDetails = async () => {
    const e = this.state.error;
    if (!e) return;
    const payload = [
      `Scope: ${this.props.scope ?? "global"}`,
      `Path:  ${window.location.pathname}${window.location.search}`,
      `Time:  ${new Date().toISOString()}`,
      `Error: ${e.name}: ${e.message}`,
      "",
      e.stack ?? "(no stack)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      // Clipboard is optional — fall through silently if denied.
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const err = this.state.error;
      const isDev = import.meta.env?.DEV;

      return (
        <div
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center justify-center py-16 px-4 text-center"
        >
          <div className="rounded-full bg-danger/10 p-4 mb-4">
            <AlertTriangle
              className="h-8 w-8 text-danger"
              aria-hidden="true"
            />
          </div>
          <h3 className="text-lg font-semibold font-heading text-text-primary">
            Something went wrong on this page
          </h3>
          <p className="text-sm text-text-muted mt-1 max-w-md">
            The rest of the app is still fine — try reloading this section,
            or head back to the dashboard.
          </p>

          {/* Dev-only stack preview — collapsed ``<details>`` so it's
              opt-in and doesn't dominate the screen. */}
          {isDev && err && (
            <details className="mt-4 w-full max-w-2xl text-left">
              <summary className="cursor-pointer text-xs text-text-muted hover:text-text-primary">
                Stack trace (development only)
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-surface border border-border p-3 text-xs text-text-primary whitespace-pre-wrap">
                {err.name}: {err.message}
                {"\n\n"}
                {err.stack ?? ""}
              </pre>
            </details>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try again
            </Button>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-surface text-text-primary border border-border hover:bg-background transition-colors"
              onClick={this.handleRetry}
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              Go to dashboard
            </Link>
            <Button variant="ghost" onClick={this.copyDetails}>
              <Copy className="h-4 w-4" aria-hidden="true" />
              Copy details
            </Button>
          </div>
        </div>
      );
    }

    // ``key`` forces a fresh subtree on each retry attempt — without it,
    // React would reconcile against the crashed tree and the same stale
    // state could re-raise immediately.
    return (
      <ErrorBoundaryContent key={this.state.retryCount}>
        {this.props.children}
      </ErrorBoundaryContent>
    );
  }
}

// Trivial identity wrapper — exists solely so we have a component to
// attach the retry ``key`` to. Inlining the fragment works but gives the
// React devtools a less informative tree.
function ErrorBoundaryContent({ children }: { children: ReactNode }) {
  return <>{children}</>;
}


/**
 * Route-level boundary — resets automatically on navigation.
 *
 * Without the location.pathname key, a user who crashed on
 * ``/employees`` would still see the fallback when they clicked
 * ``/leave`` in the sidebar, because React would keep the same
 * ErrorBoundary instance mounted. Keying on pathname remounts the
 * boundary (with fresh state) every time the route changes.
 */
interface RouteErrorBoundaryProps {
  children: ReactNode;
  scope?: string;
}

export function RouteErrorBoundary({
  children,
  scope,
}: RouteErrorBoundaryProps) {
  const location = useLocation();
  return (
    <ErrorBoundary key={location.pathname} scope={scope ?? location.pathname}>
      {children}
    </ErrorBoundary>
  );
}


/**
 * QueryErrorState — inline retry UI for failed fetches.
 *
 * Pages that swallow fetch errors into a ``setError`` piece of state
 * should render this in place of their list/detail content — it gives
 * the user the SAME retry affordance the boundary provides for render
 * crashes, but without pretending the page itself is broken.
 */
interface QueryErrorStateProps {
  title?: string;
  description?: string;
  onRetry: () => void;
  isRetrying?: boolean;
  error?: unknown;
}

export function QueryErrorState({
  title,
  description,
  onRetry,
  isRetrying,
  error,
}: QueryErrorStateProps) {
  const isOffline =
    typeof navigator !== "undefined" && navigator.onLine === false;

  // Prefer the explicit copy, then a helpful default for the offline
  // case, finally a generic reassurance.
  const resolvedTitle =
    title ?? (isOffline ? "You're offline" : "Couldn't load this");
  const resolvedDescription =
    description ??
    (isOffline
      ? "Reconnect to your network and we'll try fetching again."
      : "The request failed — this is usually transient. Give it another shot.");

  const Icon = isOffline ? WifiOff : AlertTriangle;

  // Surface the error message in dev so authors see root cause without
  // opening devtools. Strings only — don't stringify arbitrary objects.
  const devDetail =
    import.meta.env?.DEV && error instanceof Error ? error.message : null;

  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center py-16 px-4 text-center"
    >
      <div className="rounded-full bg-warning/10 p-4 mb-4">
        <Icon className="h-8 w-8 text-warning" aria-hidden="true" />
      </div>
      <h3 className="text-lg font-semibold font-heading text-text-primary">
        {resolvedTitle}
      </h3>
      <p className="text-sm text-text-muted mt-1 max-w-sm">
        {resolvedDescription}
      </p>
      {devDetail && (
        <p className="mt-2 text-xs text-text-muted font-mono max-w-md truncate">
          {devDetail}
        </p>
      )}
      <Button
        className="mt-4"
        onClick={onRetry}
        isLoading={isRetrying}
        disabled={isRetrying}
      >
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Retry
      </Button>
    </div>
  );
}


/**
 * useRetry — tiny hook that wraps an async thunk with an incrementing
 * attempt counter.
 *
 * The counter is returned so callers can shove it into a ``useEffect``
 * dep list: bumping ``attempt`` re-runs the effect, which re-fetches.
 * Pages that already store ``fetchData`` in a useCallback can simply
 * call ``retry()`` inside their QueryErrorState button handler.
 */
export function useRetry() {
  const [attempt, setAttempt] = useState(0);
  return {
    attempt,
    retry: () => setAttempt((n) => n + 1),
  };
}
